import React, { useState } from "react";
import Button, { ButtonType } from "../Assets/Button";

interface LocationSelectorProps {
  role: string;
  states: string[];
  districts: string[];
  onStatesChange: (states: string[]) => void;
  onDistrictsChange: (districts: string[]) => void;
  statesData: { state: string; districts: string[] }[];
  required?: boolean;
}

const LocationSelector: React.FC<LocationSelectorProps> = ({
  role,
  states,
  districts,
  onStatesChange,
  onDistrictsChange,
  statesData,
  required = false,
}) => {
  const [selectedState, setSelectedState] = useState<string>("");
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [tempSelectedDistricts, setTempSelectedDistricts] = useState<string[]>(
    []
  );
  const cols = role === "telecaller" ? 2 : 3;

  const hideLocations = ["admin", "factory", "account", "data entry"];
  const hideDistrictOnly = ["bdm", "sh"];

  // Don't render for roles with no location
  if (hideLocations.includes(role)) return null;

  // Roles that only require states (SH, BDM)
  if (hideDistrictOnly.includes(role)) {
    return (
      <div className="md:col-span-3">
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block mb-1 font-medium text-xs">State</label>
            <select
              value={selectedState}
              onChange={(e) => {
                const newState = e.target.value;
                if (newState && !states.includes(newState)) {
                  onStatesChange([...states, newState]);
                  setSelectedState("");
                }
              }}
              className="p-3 border rounded-lg w-full"
            >
              <option value="" disabled>
                -- Select State --
              </option>
              {statesData.map((s) => (
                <option key={s.state} value={s.state.toLowerCase()}>
                  {s.state.capitalizeWords()}
                </option>
              ))}
            </select>
          </div>

          {/* Selected States Pills */}
          <div className="flex flex-wrap gap-2">
            {states.map((state) => (
              <span key={state} className="bg-gray-200 px-2 py-1 rounded-full">
                {state.capitalizeWords()}
                <button
                  onClick={() =>
                    onStatesChange(states.filter((s) => s !== state))
                  }
                  className="ml-1 text-red-600"
                >
                  &times;
                </button>
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Full location roles (telecaller, distributor, bde)
  const currentDistrictList =
    statesData.find((s) => s.state === selectedState)?.districts || [];

  const findStateForDistrict = (district: string) => {
    return statesData.find((s) => s.districts.includes(district))?.state;
  };

  const openDistrictModal = () => {
    if (!selectedState) return;
    const existing = districts.filter((d) => currentDistrictList.includes(d));
    setTempSelectedDistricts(existing);
    setModalOpen(true);
  };

  const handleSave = () => {
    const updatedStates = states.includes(selectedState)
      ? states
      : [...states, selectedState];

    onStatesChange(updatedStates);

    const combinedDistricts = Array.from(
      new Set([...districts, ...tempSelectedDistricts])
    );
    onDistrictsChange(combinedDistricts);

    setModalOpen(false);
    setTempSelectedDistricts([]);
    setSelectedState("");
  };

  const toggleDistrict = (district: string) => {
    setTempSelectedDistricts((prev) =>
      prev.includes(district)
        ? prev.filter((d) => d !== district)
        : [...prev, district]
    );
  };

  const selectAll = () => setTempSelectedDistricts([...currentDistrictList]);
  const clearAll = () => setTempSelectedDistricts([]);

  return (
    <div className="md:col-span-3">
      <label className="block mb-1 text-xs font-medium">State</label>
      <div
        className={`grid grid-cols-1 md:grid-cols-${cols} items-center gap-6 mb-8`}
      >
        <div>
          <select
            value={selectedState}
            onChange={(e) => setSelectedState(e.target.value)}
            className=" p-3 border text-xs rounded-lg w-full"
          >
            <option value="" disabled>
              -- Select State --
            </option>
            {statesData.map((s) => (
              <option key={s.state} value={s.state.toLowerCase()}>
                {s.state.capitalizeWords()}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Button
            type={ButtonType.Submit}
            onClick={openDistrictModal}
            disabled={!selectedState}
            className={role === "telecaller" ? "text-xs py-3" : undefined}
          >
            Add Districts
          </Button>
        </div>
      </div>

      {/* Selected Pills */}
      <div className="mt-4 max-h-40 overflow-y-auto flex flex-wrap gap-2">
        {states.map((state) => (
          <span
            key={state}
            className="bg-gray-200 text-sm px-2 py-1 rounded-full"
          >
            {state.capitalizeWords()}
            <button
              onClick={() => {
                const newStates = states.filter((s) => s !== state);
                onStatesChange(newStates);
                const filteredDistricts = districts.filter(
                  (d) => findStateForDistrict(d) !== state
                );
                onDistrictsChange(filteredDistricts);
              }}
              className="ml-1 text-red-600"
            >
              &times;
            </button>
          </span>
        ))}

        {districts.map((district) => (
          <span
            key={district}
            className="bg-gray-200 text-xs px-2 py-1 rounded-full"
          >
            {district.capitalizeWords()} ({findStateForDistrict(district)})
            <button
              onClick={() =>
                onDistrictsChange(districts.filter((d) => d !== district))
              }
              className="ml-1 text-red-600"
            >
              &times;
            </button>
          </span>
        ))}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl w-1/3 max-h-[70vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              Select Districts for {selectedState}
            </h3>
            <div className="flex items-center gap-4 mb-4">
              <button onClick={selectAll}>Select All</button>
              <button onClick={clearAll}>Clear All</button>
            </div>
            <div className="overflow-y-auto h-60 pr-2 mb-6">
              <div className="grid grid-cols-2 gap-2">
                {currentDistrictList.map((d) => (
                  <label key={d} className="flex items-center">
                    <input
                      type="checkbox"
                      className="mr-2"
                      checked={tempSelectedDistricts.includes(d)}
                      onChange={() => toggleDistrict(d)}
                    />
                    {d.capitalizeWords()}
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-4">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 rounded-lg border"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationSelector;
